import React, { memo } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Badge,
  Divider,
  ListItemText,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  PhotoCamera,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useAppConfig } from '../../contexts/AppConfigContext';
import { notificationService } from '../../services/apiService';
import { getRelativeTime } from '../../utils/dateFormat';
import ProfilePictureDialog from '../Forms/ProfilePictureDialog';
const drawerWidth = 280;

const Header = ({ onMenuClick, drawerOpen = false, showBackButton = false, onBack, address = null }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, logout, setUser } = useAuth();
  const { schoolName } = useAppConfig();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = React.useState(null);
  const [profileDialogOpen, setProfileDialogOpen] = React.useState(false);

  const [notifications, setNotifications] = React.useState([]);
  const unreadCount = notifications.filter(n => Number(n.is_read) === 0).length;
  const hasLoadedNotificationsRef = React.useRef(false);

  const loadNotifications = React.useCallback(async () => {
    try {
      const res = await notificationService.getAll();
      const list = res.data || res || [];
      setNotifications(Array.isArray(list) ? list : []);
    } catch (e) {
      setNotifications([]);
    }
  }, []);

  React.useEffect(() => {
    if (hasLoadedNotificationsRef.current) return;
    hasLoadedNotificationsRef.current = true;
    loadNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty array - only run once on mount

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const markAllAsRead = async () => {
    try {
      const unread = notifications.filter(n => Number(n.is_read) === 0);
      await Promise.all(unread.slice(0, 10).map(n => notificationService.update(n.id, { is_read: 1 })));
      await loadNotifications();
    } catch {}
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotifOpen = (event) => {
    setNotifAnchorEl(event.currentTarget);
  };

  const handleNotifClose = () => {
    setNotifAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: theme.zIndex.drawer + 1,
        backgroundColor: 'background.paper',
        ml: { xs: 0, md: `${0}px` },
        width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
      }}
    >
      <Toolbar>
        {showBackButton && onBack && (
          <IconButton
            aria-label="go back"
            edge="start"
            onClick={onBack}
            sx={{ 
              mr: 2,
              color: 'text.primary',
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        )}
        <IconButton
          aria-label="open drawer"
          edge="start"
          onClick={onMenuClick}
          sx={{ 
            mr: 2,
            display: { xs: 'flex', md: 'none' },
            color: 'text.primary',
          }}
        >
          <MenuIcon />
        </IconButton>

        <Box sx={{ 
          display: { xs: 'flex', md: 'none' },
          alignItems: 'center', 
          gap: 1.5, 
          flexGrow: { xs: 1, md: 0 }
        }}>
          <Box 
            component="img" 
            src="/logo1921.png" 
            alt="School Logo" 
            sx={{ 
              width: 36, 
              height: 36,
              display: 'block',
              objectFit: 'contain',
            }} 
          />
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {schoolName}
            </Typography>
            {address && (
              <Typography variant="caption" noWrap sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                {address}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          marginLeft: 'auto'
        }}>
          <IconButton sx={{ mr: 1, color: 'text.primary' }} onClick={handleNotifOpen} aria-label="notifications">
            <Badge color="error" badgeContent={unreadCount} max={9} overlap="circular">
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <Typography variant="body2" sx={{ mr: 2, display: { xs: 'none', sm: 'block' }, color: 'text.primary', fontWeight: 500 }}>
            {user?.full_name || 'Admin'}
          </Typography>
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="primary-search-account-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            sx={{ color: 'text.primary' }}
          >
            <Avatar
              sx={{ width: 32, height: 32, cursor: 'pointer' }}
              src={user?.profile_image}
              alt={user?.full_name || 'Profile'}
              onClick={handleProfileMenuOpen}
            >
              {!user?.profile_image && (user?.full_name?.charAt(0)?.toUpperCase() || 'U')}
            </Avatar>
          </IconButton>
        </Box>

        {/* Notifications Menu */}
        <Menu
          anchorEl={notifAnchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          keepMounted
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={Boolean(notifAnchorEl)}
          onClose={handleNotifClose}
          PaperProps={{ sx: { minWidth: 320, maxWidth: 360, maxHeight: 400 } }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Notifications</Typography>
          </Box>
          <Divider />
          <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <Box sx={{ px: 2, py: 2 }}>
                <Typography variant="body2" color="text.secondary">No new notifications</Typography>
              </Box>
            ) : (
              notifications.map((n, idx) => (
                <MenuItem key={n.id} onClick={handleNotifClose} sx={{ alignItems: 'flex-start', py: 1.25 }}>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {n.title || 'Notification'}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: 12, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere', mb: 0.5 }}
                        >
                          {n.message}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">{getRelativeTime(n.created_at)}</Typography>
                      </>
                    }
                  />
                </MenuItem>
              ))
            )}
          </Box>
          <Divider />
          <MenuItem onClick={() => { markAllAsRead(); handleNotifClose(); }} sx={{ justifyContent: 'center' }}>
            <Typography variant="body2" color="primary.main">Mark all as read</Typography>
          </MenuItem>
        </Menu>

        <Menu
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem
            onClick={() => {
              handleMenuClose();
              setProfileDialogOpen(true);
            }}
          >
            <PhotoCamera sx={{ mr: 1 }} />
            Change Profile Picture
          </MenuItem>
          <MenuItem onClick={handleMenuClose}>
            <SettingsIcon sx={{ mr: 1 }} />
            Settings
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <LogoutIcon sx={{ mr: 1 }} />
            Logout
          </MenuItem>
        </Menu>

        <ProfilePictureDialog
          open={profileDialogOpen}
          onClose={() => setProfileDialogOpen(false)}
        />
      </Toolbar>
    </AppBar>
  );
};

// Memoize Header to prevent unnecessary re-renders when parent state changes
export default memo(Header);
